import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, ChevronRight, Monitor, MapPin, Shield, Clock, CheckCircle2,
  Headphones, Wifi, FileCheck, Cpu, Database, Award,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SideMenu from '../components/SideMenu';
import CookieConsent from '../components/CookieConsent';
import StickyBreadcrumb from '../components/StickyBreadcrumb';
import PrivacyPolicy from '../components/PrivacyPolicy';

const ESTADOS = [
  ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'],
  ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal'], ['ES', 'Espírito Santo'],
  ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'],
  ['MG', 'Minas Gerais'], ['PA', 'Pará'], ['PB', 'Paraíba'], ['PR', 'Paraná'],
  ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'],
  ['RS', 'Rio Grande do Sul'], ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'],
  ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins'],
];

const SERVICOS = [
  { icon: Monitor,   title: 'Suporte Windows',           desc: 'Configuração, otimização, remoção de vírus e correção de erros em Windows 10/11.' },
  { icon: FileCheck, title: 'Suporte SHOficina',         desc: 'Implantação, treinamento e suporte para SHOficina, SHNF-e, SHNFC-e e SHNFS-e.' },
  { icon: Database,  title: 'Suporte Bling ERP',         desc: 'Parceiro oficial Bling — NF-e, integrações com marketplaces, gestão financeira.' },
  { icon: Cpu,       title: 'Suporte ERPs Empresariais', desc: 'Resolução de erros, configuração e migração de sistemas ERP variados.' },
  { icon: Shield,    title: 'Certificados Digitais',     desc: 'Instalação e renovação de e-CPF, e-CNPJ (A1/A3) para emissão de NF-e.' },
  { icon: Wifi,      title: 'Redes Corporativas',        desc: 'Configuração remota de Wi-Fi Intelbras, UniFi Ubiquiti, VLANs e VPN.' },
  { icon: Headphones,title: 'Help Desk TI',              desc: 'Atendimento sob demanda ou contrato mensal para PMEs em todo o Brasil.' },
  { icon: Award,     title: 'Backup e Recuperação',      desc: 'Backup em nuvem, recuperação de dados e proteção contra ransomware.' },
];

const SUPORTE_NACIONAL_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://fernandotech.com.br/suporte-remoto-nacional",
      "url": "https://fernandotech.com.br/suporte-remoto-nacional",
      "name": "Suporte Remoto Nacional — Fernando Tech atende todo o Brasil",
      "description": "Suporte técnico remoto para empresas em todos os 27 estados brasileiros. Especialista em Windows, SHOficina, Bling ERP, certificados digitais e redes corporativas.",
      "inLanguage": "pt-BR",
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",                       "item": "https://fernandotech.com.br/" },
        { "@type": "ListItem", "position": 2, "name": "Suporte Remoto Nacional",    "item": "https://fernandotech.com.br/suporte-remoto-nacional" },
      ],
    },
    {
      "@type": "Service",
      "name": "Suporte Remoto Nacional — Fernando Tech",
      "serviceType": "Suporte Técnico Remoto de TI",
      "provider": { "@id": "https://fernandotech.com.br/#organization-extended" },
      "areaServed": { "@type": "Country", "name": "Brasil" },
      "description": "Suporte remoto especializado em Windows, ERPs (SHOficina, Bling), certificados digitais, redes e softwares empresariais.",
    },
  ],
};

export default function SuporteRemotoNacionalPage() {
  const [showPrivacy, setShowPrivacy] = useState(false);

  useEffect(() => {
    document.title = 'Suporte Remoto Nacional Brasil | Fernando Tech — Windows, SHOficina, Bling, ERPs';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    const original = meta.content;
    meta.content = 'Suporte técnico remoto para empresas em todos os 27 estados brasileiros. Especialista em Windows, SHOficina, Bling ERP, certificados digitais. Atendimento via AnyDesk, UltraViewer, TightVNC.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    const origCanonical = canonical.href;
    canonical.href = 'https://fernandotech.com.br/suporte-remoto-nacional';

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-suporte-nacional';
    script.text = JSON.stringify(SUPORTE_NACIONAL_SCHEMA);
    document.head.appendChild(script);

    return () => {
      meta.content = original;
      canonical.href = origCanonical;
      const ld = document.getElementById('ld-suporte-nacional');
      if (ld) ld.remove();
    };
  }, []);

  const waMsg = encodeURIComponent('Olá! Vim pelo site Fernando Tech (página Suporte Remoto Nacional) e preciso de suporte técnico remoto.');

  return (
    <div className="bg-slate-950 min-h-screen">
      <Navbar />

      {/* Breadcrumb — fixed logo abaixo da Navbar (mantém visível no scroll) */}
      <StickyBreadcrumb>
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 hover:text-cyan-400 transition-colors">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
          <span className="flex items-center gap-1 text-cyan-400">
            <Monitor className="w-3.5 h-3.5" /> Suporte Remoto Nacional
          </span>
        </nav>
      </StickyBreadcrumb>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-950/30 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-5">
            <MapPin className="w-3.5 h-3.5" /> Atendemos 27 estados · Brasil todo
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-5 leading-tight">
            Suporte Técnico Remoto<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              em todo o Brasil
            </span>
          </h1>

          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Resolva problemas em Windows, SHOficina, Bling ERP, certificados digitais e redes
            sem sair da sua empresa. Atendimento via AnyDesk, UltraViewer e TightVNC.
            <strong className="text-cyan-300"> 17 anos de mercado</strong>, mais de 200 atendimentos por mês.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={`https://wa.me/5516991378312?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 text-sm"
              data-testid="suporte-nacional-cta-whatsapp"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z" /></svg>
              Solicitar Suporte Agora
            </a>
            <Link
              to="/comprar"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-cyan-500 text-white font-bold rounded-full transition-all hover:scale-105 text-sm"
            >
              Conhecer SHOficina
            </Link>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="py-14 bg-slate-900/40 border-y border-slate-800/60">
        <div className="container mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-400 text-center mb-3">O que resolvemos</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">
            Suporte remoto especializado para o seu negócio
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICOS.map(s => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="p-5 bg-slate-800/50 border border-slate-700/50 rounded-2xl hover:border-cyan-500/30 transition-all"
                  data-testid={`servico-${s.title}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-white font-semibold text-sm mb-1">{s.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Públicos atendidos */}
      <section className="py-14">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">
            Para quem é o suporte remoto Fernando Tech?
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
            {['Microempresas', 'Pequenas Empresas', 'Oficinas Mecânicas', 'Auto Centers', 'Escritórios', 'Profissionais Autônomos'].map(p => (
              <div key={p} className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto mb-1.5" />
                <p className="text-white text-xs font-semibold">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cobertura nacional */}
      <section className="py-14 bg-slate-900/40 border-y border-slate-800/60">
        <div className="container mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-400 text-center mb-3">Cobertura nacional</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-3">
            Atendimento 100% remoto em todos os 27 estados
          </h2>
          <p className="text-slate-400 text-center max-w-2xl mx-auto mb-10 text-sm">
            Não importa onde sua empresa esteja: nosso time se conecta ao seu computador via AnyDesk, UltraViewer ou TightVNC e resolve o problema rapidamente.
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2 max-w-5xl mx-auto">
            {ESTADOS.map(([uf, nome]) => (
              <div
                key={uf}
                className="bg-slate-800/50 border border-slate-700 hover:border-cyan-500/40 rounded-lg p-2 text-center transition-all"
                title={`Atendemos ${nome}`}
                data-testid={`estado-${uf}`}
              >
                <p className="text-cyan-400 font-bold text-sm">{uf}</p>
                <p className="text-slate-500 text-[10px] leading-tight">{nome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-950/20 to-slate-950 relative overflow-hidden">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-5">
            <Clock className="w-3.5 h-3.5" /> Atendimento ágil
          </div>
          <h2 className="text-white font-extrabold text-2xl sm:text-3xl mb-3 leading-tight">
            Precisa de suporte agora? Estamos online.
          </h2>
          <p className="text-slate-400 text-sm mb-8 leading-relaxed">
            Seg–Sex 8h–18h · Sáb 8h–12h · Resposta em até 15 min no WhatsApp
          </p>
          <a
            href={`https://wa.me/5516991378312?text=${waMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg transition-all hover:scale-105 text-sm"
            data-testid="suporte-nacional-cta-final"
          >
            Falar pelo WhatsApp
          </a>
        </div>
      </section>

      <Footer onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <SideMenu />
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacy(true)} />
      <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
}
